import { ProfileAPI } from './api'
import { alert } from '@codeleap/modals'
import { RemoveAvatarAction, ResetPasswordAction, UpdateAvatarAction } from './types'
import { setFeatures } from './stores'
import { ReactQuery } from '@codeleap/query'

export class ProfileServices {
  protected api: ProfileAPI
  protected LOCAL: boolean

  constructor(api: ProfileAPI) {
    this.api = api
  }

  async resetPassword({ profile, setStatus, refreshProfile }: ResetPasswordAction) {
    function errorHandling() {
      alert.error({
        title: 'Error',
        body: 'Failed to send password reset email. Please try again.',
      })
    }

    try {
      setStatus('loading')
      let email = profile?.email

      if (!email) {
        const res = await refreshProfile?.()
        email = res?.email
      }

      const response = await this.api.resetPassword(email)

      if (response.ok) {
        alert.info({
          title: 'Success!',
          body: 'A password reset email has been sent to your email address.',
          options: [
            { text: 'Confirm', style: 'default' },
          ],
        })
      } else {
        console.error(response.status, response?.data)
        errorHandling()
      }
    } catch (error: any) {
      console.error('Failed to send password reset email', error)
      errorHandling()
    } finally {
      setStatus('idle')
    }
  }

  async uploadAvatar({file, setStatus, refreshProfile}: UpdateAvatarAction) {
    try {
      setStatus('loading')
      await this.api.updateAvatar(file)
      await refreshProfile?.()
      setStatus('done')
    } catch (e) {
      alert.error({
        title: 'Error',
        body: 'Failed to update avatar. Please try again.',
      })
      setStatus('idle')
    }
  }

  async removeAvatar({ setStatus, refreshProfile }: RemoveAvatarAction) {
    try {
      setStatus('loading')
      const response = await this.api.removeAvatar()
      if (!response.ok) {
        console.error(response.status, response?.data)
        throw new Error('Failed to remove avatar')
      }
      await refreshProfile?.()
      setStatus('done')
    } catch (e) {
      alert.error({
        title: 'Error',
        body: 'Failed to remove avatar. Please try again.',
      })
      setStatus('idle')
    }
  }

  async loadFeatures() {
    const res = await this.api.listFeatures()
    if (res.ok) {
      setFeatures(res.data)
    }
  }

  useApps(options?: ReactQuery.UndefinedInitialDataOptions) {
    const query = ReactQuery.useQuery({
      queryKey: ['profileApps'],
      
      queryFn: async () => {
        const res = await this.api.listApps()
        if (res.ok) {
          return res.data
        }
        return null
      },
      ...options,
    })

    return query
  }
}
